import type { QuizConfig } from '@/types/form.types'
import { getTemplatesForExperienceMode } from '@/config/quiz-templates.config'

export interface ExperienceLibraryRecord {
  localKey: string
  familyId?: string
  recordType?: 'draft' | 'published-version'
  lineageId?: string
  versionNumber?: number
  name: string
  status: 'draft' | 'published'
  publishedAt?: string
  forkedFromId?: string
  archivedAt?: string
  experienceMode: QuizConfig['experienceMode']
  templateFamilyId?: string
  campaignName?: string
  rounds: number
  stages: number
  questions: number
  updatedAt: string
  quizConfig: QuizConfig
  usage?: ExperienceUsageSummary
  orgId?: string
  tenantId?: string
}

export interface ExperienceUsageCampaign {
  campaignId: string
  campaignName: string
  updatedAt?: string | null
}

export interface ExperienceUsageSummary {
  exactVersionCount: number
  lineageCount: number
  campaigns: ExperienceUsageCampaign[]
}

const STORAGE_KEY = 'irlExperienceLibrary'
const MAX_RECORDS = 24

function generateLocalKey(): string {
  return `exp-${Math.random().toString(36).slice(2, 9)}`
}

function canUseStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

function cloneQuizConfig(config: QuizConfig): QuizConfig {
  return JSON.parse(JSON.stringify(config)) as QuizConfig
}

function normalizeMode(mode?: QuizConfig['experienceMode']): 'quiz' | 'survey' | 'all-in-one' {
  if (mode === 'survey') return 'survey'
  if (mode === 'all-in-one') return 'all-in-one'
  return 'quiz'
}

function isGenericExperienceName(name?: string): boolean {
  const normalized = (name || '').trim().toLowerCase()
  return normalized === ''
    || normalized === 'quiz experience'
    || normalized === 'survey experience'
    || normalized === 'all-in-one experience'
}

function deriveExperienceName(config: QuizConfig): string {
  const mode = normalizeMode(config.experienceMode)
  const templateId = config.experienceTemplateFamilyId || config.quizTemplateId
  const templateName = getTemplatesForExperienceMode(mode).find((template) => template.id === templateId)?.name

  if (templateName) {
    if (mode === 'survey') {
      return templateName.toLowerCase().includes('survey') ? templateName : `${templateName} Survey`
    }
    if (mode === 'all-in-one') {
      return templateName.toLowerCase().includes('experience') ? templateName : `${templateName} Experience`
    }
    return templateName.toLowerCase().includes('quiz') ? templateName : `${templateName} Quiz`
  }

  if (mode === 'survey') return 'Survey Experience'
  if (mode === 'all-in-one') return 'All-In-One Experience'
  return 'Quiz Experience'
}

function sortExperienceLibraryRecords(records: ExperienceLibraryRecord[]): ExperienceLibraryRecord[] {
  return [...records].sort((a, b) => {
    const statusRank = (status: ExperienceLibraryRecord['status']) => status === 'published' ? 0 : 1
    const statusDiff = statusRank(a.status) - statusRank(b.status)
    if (statusDiff !== 0) return statusDiff

    const updatedDiff = new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    if (!Number.isNaN(updatedDiff) && updatedDiff !== 0) return updatedDiff

    return a.name.localeCompare(b.name)
  })
}

export function materializeExperienceIdentity(config: QuizConfig): QuizConfig {
  const quizConfig = cloneQuizConfig(config)
  if (!quizConfig.experienceDefinitionLocalKey) {
    quizConfig.experienceDefinitionLocalKey = generateLocalKey()
  }
  if (!quizConfig.experienceDefinitionName?.trim()) {
    quizConfig.experienceDefinitionName = deriveExperienceName(quizConfig)
  } else if (isGenericExperienceName(quizConfig.experienceDefinitionName)) {
    quizConfig.experienceDefinitionName = deriveExperienceName(quizConfig)
  }
  if (!quizConfig.experienceDefinitionStatus) {
    quizConfig.experienceDefinitionStatus = 'published'
  }
  if (!quizConfig.experienceDefinitionLastSavedAt) {
    quizConfig.experienceDefinitionLastSavedAt = new Date().toISOString()
  }
  return quizConfig
}

export function listExperienceLibraryRecords(options?: { includeArchived?: boolean }): ExperienceLibraryRecord[] {
  if (!canUseStorage()) return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    const records = Array.isArray(parsed) ? parsed as ExperienceLibraryRecord[] : []
    const normalized = records.map((record) => ({
      ...record,
      name: isGenericExperienceName(record.name) ? deriveExperienceName(record.quizConfig) : record.name,
    }))
    return sortExperienceLibraryRecords(options?.includeArchived ? normalized : normalized.filter((item) => !item.archivedAt))
  } catch {
    return []
  }
}

export function replaceExperienceLibraryRecords(records: ExperienceLibraryRecord[]): void {
  if (!canUseStorage()) return
  const normalized = records.map((record) => ({
    ...record,
    name: isGenericExperienceName(record.name) ? deriveExperienceName(record.quizConfig) : record.name,
  }))
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sortExperienceLibraryRecords(normalized)))
}

export function upsertExperienceLibraryRecord(record: ExperienceLibraryRecord): ExperienceLibraryRecord | null {
  if (!canUseStorage()) return null
  const existing = listExperienceLibraryRecords({ includeArchived: true }).filter((item) => item.localKey !== record.localKey)
  const normalized: ExperienceLibraryRecord = {
    ...record,
    name: isGenericExperienceName(record.name) ? deriveExperienceName(record.quizConfig) : record.name,
  }
  const next = [normalized, ...existing].slice(0, MAX_RECORDS)
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sortExperienceLibraryRecords(next)))
  return normalized
}

export function getExperienceLibraryRecord(localKey: string): ExperienceLibraryRecord | null {
  return listExperienceLibraryRecords().find((item) => item.localKey === localKey) || null
}

export function buildExperienceLibraryRecord(input: {
  quizConfig: QuizConfig
  campaignName?: string
  seed?: Partial<ExperienceLibraryRecord>
  orgId?: string
  tenantId?: string
}): ExperienceLibraryRecord | null {
  const quizConfig = materializeExperienceIdentity(input.quizConfig)
  const localKey = quizConfig.experienceDefinitionLocalKey
  if (!localKey) return null
  const existingRecord = listExperienceLibraryRecords({ includeArchived: true }).find((item) => item.localKey === localKey)

  const rounds = quizConfig.rounds.length
  const stages = quizConfig.rounds.reduce((sum, round) => sum + round.stages.length, 0)
  const questions = quizConfig.rounds.reduce(
    (sum, round) => sum + round.stages.reduce((stageSum, stage) => stageSum + stage.questions.length, 0),
    0,
  )

  const record: ExperienceLibraryRecord = {
    localKey,
    lineageId: input.seed?.lineageId || existingRecord?.lineageId || quizConfig.experienceDefinitionId || localKey,
    familyId: input.seed?.familyId || existingRecord?.familyId || quizConfig.experienceDefinitionId || localKey,
    recordType: input.seed?.recordType || existingRecord?.recordType || (quizConfig.experienceDefinitionStatus === 'published' ? 'published-version' : 'draft'),
    versionNumber: input.seed?.versionNumber || existingRecord?.versionNumber || (quizConfig.experienceDefinitionVersionId ? Number.parseInt(quizConfig.experienceDefinitionVersionId.replace(/^v/, ''), 10) || 1 : 1),
    name: isGenericExperienceName(quizConfig.experienceDefinitionName)
      ? deriveExperienceName(quizConfig)
      : quizConfig.experienceDefinitionName || 'Untitled Experience',
    status: quizConfig.experienceDefinitionStatus || 'published',
    publishedAt: input.seed?.publishedAt || existingRecord?.publishedAt || (quizConfig.experienceDefinitionStatus === 'published' ? quizConfig.experienceDefinitionLastSavedAt : undefined),
    forkedFromId: input.seed?.forkedFromId || existingRecord?.forkedFromId,
    experienceMode: quizConfig.experienceMode || 'quiz',
    templateFamilyId: quizConfig.experienceTemplateFamilyId || quizConfig.quizTemplateId,
    campaignName: input.campaignName,
    rounds,
    stages,
    questions,
    updatedAt: quizConfig.experienceDefinitionLastSavedAt || new Date().toISOString(),
    quizConfig,
    orgId: input.orgId || input.seed?.orgId || existingRecord?.orgId,
    tenantId: input.tenantId || input.seed?.tenantId || existingRecord?.tenantId,
  }

  return record
}

export function saveExperienceLibraryRecord(input: {
  quizConfig: QuizConfig
  campaignName?: string
  seed?: Partial<ExperienceLibraryRecord>
  orgId?: string
  tenantId?: string
}): ExperienceLibraryRecord | null {
  if (!canUseStorage()) return null

  const record = buildExperienceLibraryRecord(input)
  if (!record) return null

  const existing = listExperienceLibraryRecords({ includeArchived: true }).filter((item) => item.localKey !== record.localKey)
  const next = [record, ...existing].slice(0, MAX_RECORDS)
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  return record
}

export function archiveExperienceLibraryRecord(localKey: string): void {
  if (!canUseStorage()) return
  const next = listExperienceLibraryRecords({ includeArchived: true }).map((item) =>
    item.localKey === localKey
      ? { ...item, archivedAt: new Date().toISOString() }
      : item,
  )
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
}

export function restoreExperienceLibraryRecord(localKey: string): void {
  if (!canUseStorage()) return
  const next = listExperienceLibraryRecords({ includeArchived: true }).map((item) =>
    item.localKey === localKey
      ? { ...item, archivedAt: undefined }
      : item,
  )
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
}

export function permanentlyDeleteExperienceLibraryRecord(localKey: string): void {
  if (!canUseStorage()) return
  const next = listExperienceLibraryRecords({ includeArchived: true }).filter((item) => item.localKey !== localKey)
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
}

export function duplicateExperienceLibraryRecord(localKey: string): ExperienceLibraryRecord | null {
  const record = getExperienceLibraryRecord(localKey)
  if (!record) return null

  const nextVersion = (record.versionNumber || 1) + 1
  const duplicatedConfig = materializeExperienceIdentity({
    ...cloneQuizConfig(record.quizConfig),
    experienceDefinitionLocalKey: undefined,
    experienceDefinitionName: `${record.name} Copy`,
    experienceDefinitionStatus: 'draft',
    experienceDefinitionId: record.lineageId || record.localKey,
    experienceDefinitionVersionId: `v${nextVersion}`,
    experienceDefinitionLastSavedAt: new Date().toISOString(),
  })

  return saveExperienceLibraryRecord({
    quizConfig: duplicatedConfig,
    campaignName: record.campaignName,
    seed: {
      lineageId: record.lineageId || record.localKey,
      familyId: record.familyId || record.lineageId || record.localKey,
      recordType: 'draft',
      versionNumber: nextVersion,
      forkedFromId: record.localKey,
    },
  })
}
